'use client';

import { ReactNode, useEffect, useState } from 'react';

import { TERMLY_SCRIPT_ID } from './constants';

const TERMLY_POLL_INTERVAL_MS = 50;
const TERMLY_POLL_TIMEOUT_MS = 10_000;

type Props = {
  scriptUrl?: string;
  children: ReactNode;
};

function ensureTermlyScript(scriptUrl: string) {
  const existing = document.getElementById(TERMLY_SCRIPT_ID);
  if (existing) return;

  const script = document.createElement('script');
  script.id = TERMLY_SCRIPT_ID;
  script.src = scriptUrl;
  script.async = true;
  document.head.prepend(script);
}

// Holds tracking scripts until Termly is on the page. Client-rendered pages
// (e.g. 404) never run `beforeInteractive` scripts, so the embed is injected here.
export function TermlyGate({ scriptUrl, children }: Props) {
  const [ready, setReady] = useState(!scriptUrl);

  useEffect(() => {
    if (!scriptUrl || ready) return;

    ensureTermlyScript(scriptUrl);

    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      if (window.Termly) {
        setReady(true);
      } else if (Date.now() - startedAt < TERMLY_POLL_TIMEOUT_MS) {
        return;
      }

      window.clearInterval(timer);
    }, TERMLY_POLL_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [scriptUrl, ready]);

  if (!ready) return null;

  return <>{children}</>;
}
